import type { ErasureConfirm, ErasureResult, ErasureSummary, OrgConsentView, PersonExport, PrivacyRequestView, RetentionStatusView, SummaryStatusView } from '@takeit4me/shared'
import type { Http } from '../http'

type In<T extends { _zod: { input: unknown } }> = T['_zod']['input']
export type ErasureBody = In<typeof ErasureConfirm>
export type SummaryTargetQuery = { person_id: string } | { organisation_id: string }

const enc = encodeURIComponent

/** Console API methods: workstream G (export, erasure, retention, organisation consent, summaries). */
export interface PrivacyApi {
  /** Consent across an organisation's people. */
  organisationConsents(organisationId: string): Promise<OrgConsentView>
  summaryStatus(target: SummaryTargetQuery): Promise<SummaryStatusView>
  refreshSummary(target: SummaryTargetQuery): Promise<SummaryStatusView>
  /** Everything held about a person, as one JSON document (admin, supervisor). */
  exportPerson(personId: string): Promise<PersonExport>
  /** What erasing a person would remove and keep, and anything blocking it (admin). */
  erasurePlan(personId: string): Promise<ErasureSummary>
  erasePerson(personId: string, body: ErasureBody): Promise<ErasureResult>
  retentionStatus(): Promise<RetentionStatusView>
  runRetention(): Promise<RetentionStatusView>
  privacyRequests(): Promise<PrivacyRequestView[]>
}
export const privacyMethods = (http: Http): PrivacyApi => ({
  organisationConsents: (id) => http.get<OrgConsentView>(`/crm/organisations/${enc(id)}/consents`),
  summaryStatus: (t) => http.get<SummaryStatusView>('/crm/summary', t),
  refreshSummary: (t) => http.post<SummaryStatusView>(`/crm/summary/refresh?${'person_id' in t ? `person_id=${enc(t.person_id)}` : `organisation_id=${enc(t.organisation_id)}`}`),
  exportPerson: (id) => http.get<PersonExport>(`/crm/people/${enc(id)}/export`),
  erasurePlan: (id) => http.get<ErasureSummary>(`/crm/people/${enc(id)}/erasure`),
  erasePerson: (id, body) => http.post<ErasureResult>(`/crm/people/${enc(id)}/erase`, body),
  retentionStatus: () => http.get<RetentionStatusView>('/privacy/retention'),
  runRetention: () => http.post<RetentionStatusView>('/privacy/retention/run'),
  privacyRequests: () => http.get<PrivacyRequestView[]>('/privacy/requests'),
})
